import React, { useEffect, useState } from 'react';
import { ChevronRight, PanelLeft, PanelLeftClose } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { menu_categories } from '../../misc/objs';
import { neo4jService } from '../../services/neo4jService';
import './CategorySidebar.css';

const CategorySidebar = ({ visible = true, onClose }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 768);
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      try {
        const availableCategories = await neo4jService.getAvailableCategories();
        const availableCategorySet = new Set(availableCategories);

        const matchedCategories = menu_categories
          .map(category => {
            const subCategories = category.subCategories.filter(subcategory =>
              availableCategorySet.has(subcategory)
            );

            if (subCategories.length === 0) {
              return null;
            }

            return {
              ...category,
              subCategories
            };
          })
          .filter(Boolean);

        const knownSubcategories = new Set(
          matchedCategories.flatMap(category => category.subCategories)
        );

        const uncategorized = availableCategories
          .filter(category => !knownSubcategories.has(category))
          .map((category, index) => ({
            id: `dynamic-${index}`,
            name: category,
            icon: null,
            subCategories: [category]
          }));

        if (isMounted) {
          setCategories([...matchedCategories, ...uncategorized]);
        }
      } catch (error) {
        console.error('Error loading sidebar categories:', error);
        if (isMounted) {
          setCategories([]);
        }
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileViewport(mobile);

      if (mobile) {
        setIsDesktopCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!visible || !isMobileViewport) {
      document.body.classList.remove('category-sidebar-open');
      return undefined;
    }

    document.body.classList.add('category-sidebar-open');

    return () => {
      document.body.classList.remove('category-sidebar-open');
    };
  }, [visible]);

  const handleCategoryClick = (category, subcategory) => {
    navigate(`/category/${subcategory.toLowerCase()}`);
    if (onClose) {
      onClose();
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setActiveCategory(prev => (prev === categoryId ? null : categoryId));
  };

  const toggleDesktopCollapsed = () => {
    if (!isMobileViewport) {
      setIsDesktopCollapsed(prev => !prev);
      setActiveCategory(null);
    }
  };

  return (
    <>
      <div
        className={`category-sidebar-overlay ${isMobileViewport && visible ? 'open' : ''}`}
        onClick={onClose}
      />
      <aside
        className={`category-sidebar ${!isMobileViewport && isDesktopCollapsed ? 'collapsed' : ''} ${isMobileViewport && !visible ? 'hidden' : ''} ${isMobileViewport && visible ? 'mobile-open' : ''}`}
        aria-hidden={isMobileViewport ? !visible : false}
      >
        {!isMobileViewport && (
          <button
            type="button"
            className="category-sidebar-toggle"
            onClick={toggleDesktopCollapsed}
            aria-label={isDesktopCollapsed ? 'Expand categories' : 'Collapse categories'}
            title={isDesktopCollapsed ? 'Expand categories' : 'Collapse categories'}
          >
            {isDesktopCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        )}
        <div className="category-sidebar-header">
          <h2>Browse Categories</h2>
          <button
            type="button"
            className="category-sidebar-close"
            onClick={onClose}
            aria-label="Close categories"
          >
            <PanelLeftClose size={18} />
          </button>
        </div>
        <div className="category-list">
          {categories.map(category => (
            <div
              key={category.id}
              className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
            >
              <button
                type="button"
                className="category-content"
                onClick={() => handleCategoryToggle(category.id)}
                title={category.name}
              >
                <span className={`category-icon-shell ${category.icon ? 'has-icon' : 'initial-only'}`}>
                  {category.icon || <span className="category-initial">{category.name.charAt(0)}</span>}
                </span>
                <span className="category-name">{category.name}</span>
                <ChevronRight className="category-arrow" size={16} />
              </button>
              
              {activeCategory === category.id && (
                <div className="subcategories">
                  {category.subCategories.map(subcategory => (
                    <button
                      type="button"
                      key={subcategory}
                      className="subcategory-item"
                      onClick={() => handleCategoryClick(category.name, subcategory)}
                    >
                      {subcategory}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default CategorySidebar;
